// ============================================
// LUDO BOARD CONFIG
// ============================================

export const BOARD_SIZE = 15;


// ============================================
// 52-CELL COMMON LUDO PATH
// ============================================
//
// The path starts at Green's starting cell.
//
// IMPORTANT:
// The first cell is (6,1)
// NOT (6,0).
//
// (6,0) is the final cell before
// returning to the starting point.
// ============================================

export const COMMON_PATH = [

  // ------------------------------------------
  // GREEN → TOP
  // ------------------------------------------

  { row: 6, col: 1 }, // 0  GREEN START
  { row: 6, col: 2 }, // 1
  { row: 6, col: 3 }, // 2
  { row: 6, col: 4 }, // 3
  { row: 6, col: 5 }, // 4

  { row: 5, col: 6 }, // 5
  { row: 4, col: 6 }, // 6
  { row: 3, col: 6 }, // 7
  { row: 2, col: 6 }, // 8
  { row: 1, col: 6 }, // 9
  { row: 0, col: 6 }, // 10

  // ------------------------------------------
  // TOP
  // ------------------------------------------

  { row: 0, col: 7 }, // 11
  { row: 0, col: 8 }, // 12

  // ------------------------------------------
  // YELLOW START → RIGHT
  // ------------------------------------------

  { row: 1, col: 8 }, // 13  YELLOW START
  { row: 2, col: 8 }, // 14
  { row: 3, col: 8 }, // 15
  { row: 4, col: 8 }, // 16
  { row: 5, col: 8 }, // 17

  { row: 6, col: 9 },  // 18
  { row: 6, col: 10 }, // 19
  { row: 6, col: 11 }, // 20
  { row: 6, col: 12 }, // 21
  { row: 6, col: 13 }, // 22
  { row: 6, col: 14 }, // 23

  // ------------------------------------------
  // RIGHT
  // ------------------------------------------

  { row: 7, col: 14 }, // 24
  { row: 8, col: 14 }, // 25

  // ------------------------------------------
  // BLUE START → BOTTOM
  // ------------------------------------------

  { row: 8, col: 13 }, // 26  BLUE START
  { row: 8, col: 12 }, // 27
  { row: 8, col: 11 }, // 28
  { row: 8, col: 10 }, // 29
  { row: 8, col: 9 },  // 30

  { row: 9, col: 8 },  // 31
  { row: 10, col: 8 }, // 32
  { row: 11, col: 8 }, // 33
  { row: 12, col: 8 }, // 34
  { row: 13, col: 8 }, // 35
  { row: 14, col: 8 }, // 36

  // ------------------------------------------
  // BOTTOM
  // ------------------------------------------

  { row: 14, col: 7 }, // 37
  { row: 14, col: 6 }, // 38

  // ------------------------------------------
  // RED START → LEFT
  // ------------------------------------------

  { row: 13, col: 6 }, // 39  RED START
  { row: 12, col: 6 }, // 40
  { row: 11, col: 6 }, // 41
  { row: 10, col: 6 }, // 42
  { row: 9, col: 6 },  // 43

  { row: 8, col: 5 }, // 44
  { row: 8, col: 4 }, // 45
  { row: 8, col: 3 }, // 46
  { row: 8, col: 2 }, // 47
  { row: 8, col: 1 }, // 48
  { row: 8, col: 0 }, // 49

  { row: 7, col: 0 }, // 50
  { row: 6, col: 0 }, // 51
];


// ============================================
// PLAYER START POSITIONS
// ============================================

export const PLAYER_START = {
  green: 0,
  yellow: 13,
  blue: 26,
  red: 39,
};


// ============================================
// SAFE / STAR CELLS
// ============================================

export const SAFE_CELLS = [
  0,
  8,
  13,
  21,
  26,
  34,
  39,
  47,
];


// ============================================
// GET BOARD CELL
// ============================================

export function getBoardPosition(pathIndex) {
  return COMMON_PATH[pathIndex];
}


// ============================================
// CHECK SAFE CELL
// ============================================

export function isSafeCell(pathIndex) {
  return SAFE_CELLS.includes(pathIndex);
}


// ============================================
// GRID → PERCENTAGE
// ============================================

export function gridToPercent(row, col) {
  return {
    left: `${((col + 0.5) / BOARD_SIZE) * 100}%`,
    top: `${((row + 0.5) / BOARD_SIZE) * 100}%`,
  };
}


// ============================================
// PATH INDEX → VISUAL POSITION
// ============================================

export function getPathVisualPosition(pathIndex) {

  const position =
    getBoardPosition(pathIndex);

  if (!position) {
    return null;
  }

  return gridToPercent(
    position.row,
    position.col
  );
}


// ============================================
// MOVE
// ============================================

export function getNextStep(
  currentStep,
  diceValue
) {
  return currentStep + diceValue;
}

// ============================================
// GET GLOBAL PATH INDEX FOR PLAYER
// ============================================
//
// tokenStep is relative to that player's
// starting position.
//
// Example:
//
// Green step 0 → path 0
// Yellow step 0 → path 13
// Blue step 0 → path 26
// Red step 0 → path 39
// ============================================

export function getPlayerPathIndex(
  color,
  tokenStep
) {

  const start =
    PLAYER_START[color];

  if (start === undefined) {
    return null;
  }

  if (
    tokenStep < 0 ||
    tokenStep >= 52
  ) {
    return null;
  }

  return (
    start + tokenStep
  ) % 52;
}

// ============================================
// GET PLAYER TOKEN VISUAL POSITION
// ============================================

export function getPlayerPathVisualPosition(
  color,
  tokenStep
) {

  // ========================================
  // COMMON PATH
  // ========================================

  if (tokenStep < 51) {

    const globalPathIndex =
      getPlayerPathIndex(
        color,
        tokenStep
      );

    if (
      globalPathIndex === null
    ) {
      return null;
    }

    return getPathVisualPosition(
      globalPathIndex
    );
  }


  // ========================================
  // PLAYER HOME LANE
  // ========================================

  return getPlayerHomeVisualPosition(
    color,
    tokenStep
  );
}

// ============================================
// PLAYER HOME LANES
// ============================================
//
// IMPORTANT:
// Each player's colored home lane has
// 5 playable cells.
//
// The center triangle is NOT part of the
// colored home lane.
//
// 52 → first home cell
// 53 → second
// 54 → third
// 55 → fourth
// 56 → fifth
// 57 → FINISHED / CENTER
// ============================================

export const HOME_PATHS = {
  green: [
    { row: 7, col: 1 }, // 51
    { row: 7, col: 2 }, // 52
    { row: 7, col: 3 }, // 53
    { row: 7, col: 4 }, // 54
    { row: 7, col: 5 }, // 55
  ],

  yellow: [
    { row: 1, col: 7 }, // 51
    { row: 2, col: 7 }, // 52
    { row: 3, col: 7 }, // 53
    { row: 4, col: 7 }, // 54
    { row: 5, col: 7 }, // 55
  ],

  blue: [
    { row: 7, col: 13 }, // 51
    { row: 7, col: 12 }, // 52
    { row: 7, col: 11 }, // 53
    { row: 7, col: 10 }, // 54
    { row: 7, col: 9 },  // 55
  ],

  red: [
    { row: 13, col: 7 }, // 51
    { row: 12, col: 7 }, // 52
    { row: 11, col: 7 }, // 53
    { row: 10, col: 7 }, // 54
    { row: 9, col: 7 },  // 55
  ],
};

// ============================================
// GET PLAYER HOME POSITION
// ============================================

export function getPlayerHomeVisualPosition(
  color,
  homeStep
) {

  const path =
    HOME_PATHS[color];

  if (!path) {
    return null;
  }

  const index =
    homeStep - 51;

  if (
    index < 0 ||
    index >= path.length
  ) {
    return null;
  }

  const position =
    path[index];

  return gridToPercent(
    position.row,
    position.col
  );
}

// ============================================
// GET GLOBAL POSITION OF TOKEN
// ============================================
//
// Returns the physical common-path cell.
//
// Home-lane tokens return null because they
// cannot be captured.
// ============================================

export function getTokenGlobalPathIndex(token) {

  if (!token) {
    return null;
  }

  if (
    token.status !== "active" ||
    token.step < 0 ||
    token.step >= 51
  ) {
    return null;
  }

  return getPlayerPathIndex(
    token.color,
    token.step
  );
}